import React from 'react';
import { useLocation } from 'react-router';
import { Menu } from 'lucide-react';
import { useWorkspace } from '@/contexts/WorkspaceContext';

interface TopBarProps {
  onMenuClick: () => void;
}

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/settings': 'Settings',
};

export function TopBar({ onMenuClick }: TopBarProps) {
  const location = useLocation();
  const { workspace, usage, tierLimits } = useWorkspace();

  const pageTitle = PAGE_TITLES[location.pathname] || 'JigFlow';

  // Format usage display
  const downloadLimit = tierLimits.downloadsPerMonth;
  const downloadCount = usage?.downloadCount || 0;
  const usageText = downloadLimit === Infinity
    ? `${downloadCount} downloads this month`
    : `${downloadCount} / ${downloadLimit} downloads this month`;

  return (
    <header className="h-16 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 flex items-center justify-between">
      {/* Left side: Menu button (mobile) + Page title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          aria-label="Toggle menu"
        >
          <Menu className="w-5 h-5" />
        </button>

        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          {pageTitle}
        </h1>
      </div>

      {/* Right side: Workspace name + Usage meter */}
      <div className="flex items-center gap-6">
        {/* Usage meter */}
        <div className="hidden sm:flex items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400">{usageText}</span>
          {downloadLimit !== Infinity && (
            <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${Math.min((downloadCount / downloadLimit) * 100, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Workspace name */}
        {workspace && (
          <div className="hidden md:block text-sm font-medium text-gray-700 dark:text-gray-300">
            {workspace.name}
          </div>
        )}
      </div>
    </header>
  );
}
